import { useEffect, useState } from 'react';
import type { PriWord } from '@/copy/hero-pri-words';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

type Phase = 'typing' | 'holding' | 'clearing';

/* Seeding the first word complete is correct in exactly ONE situation: this
 * mount is hydrating prerendered markup that already contains the word. Any
 * other mount should type it in, and three conditions together identify that
 * one situation.
 *
 * The <html> marker alone is not enough — it lives for the tab's lifetime, so
 * it is still set after a client-side navigation, where nothing was
 * prerendered and there is no mismatch to avoid.
 *
 * The initial pathname is what separates "arrived here" from "navigated here".
 * A visitor who lands on /en/privacy (marker set) and clicks home reaches /en/
 * with no prerendered hero, so the word must type in. Read at module load,
 * guarded because this module is also imported by the build-time prerender
 * entry, where `location` does not exist.
 *
 * hydrationPending covers the remaining case: navigating away from /en/ and
 * back lands on the initial pathname again, but that render is not hydration.
 * Flipped in an effect, never during render — React may discard and restart a
 * render pass, and a state initializer that mutated module state would then
 * seed 0 against a DOM still holding the full word: the exact mismatch this
 * seeding exists to prevent. */
const INITIAL_PATH = typeof document === 'undefined' ? null : window.location.pathname;
let hydrationPending =
  typeof document !== 'undefined' &&
  document.documentElement.hasAttribute('data-pn-prerendered');

interface TypewriterProps {
  words: readonly PriWord[];
  prefix: string;
  typeDelay?: number;
  backDelay?: number;
  holdDelay?: number;
  finalHoldDelay?: number;
}

export default function Typewriter({
  words,
  prefix,
  typeDelay = 95,
  backDelay = 48,
  holdDelay = 1700,
  finalHoldDelay = 3100,
}: TypewriterProps) {
  const reduceMotion = usePrefersReducedMotion();
  const [wordIndex, setWordIndex] = useState(0);
  /* Seeded to the full word when the markup was prerendered: the animated
   * branch would otherwise render an EMPTY suffix on the server, shipping
   * /en/'s headline word missing from the static HTML — invisible to a
   * length-based check, because the rest of the page is thousands of chars.
   *
   * The gate is the hydration test above, deliberately NOT
   * `typeof window === 'undefined'`. hydrateRoot runs in the browser, so a
   * window check would seed 0 during hydration while the server emitted the
   * full word: React 18 does not repair text mismatches in a production build,
   * so the DOM would keep "Privacy" while state said 0, and the first timer
   * tick would visibly collapse the headline and re-type it.
   *
   * The animation still runs: charCount === suffix.length enters 'typing',
   * which immediately schedules 'holding' -> 'clearing' -> next word. */
  const [charCount, setCharCount] = useState(() => {
    const full = Math.max(0, (words[0]?.en.length ?? 0) - prefix.length);
    if (typeof document === 'undefined') return full; // build-time render
    const isHydrating = hydrationPending && window.location.pathname === INITIAL_PATH;
    return isHydrating ? full : 0;
  });
  const [phase, setPhase] = useState<Phase>('typing');

  /* Hydration is over once anything has mounted. Deliberately its own effect
   * with an empty dependency array, and deliberately not done in the state
   * initializer above: initializers must stay pure. */
  useEffect(() => {
    hydrationPending = false;
  }, []);

  useEffect(() => {
    if (reduceMotion) return;
    const current = words[wordIndex];
    if (!current) return;
    const suffix = current.en.slice(prefix.length);
    if (phase === 'typing') {
      if (charCount < suffix.length) {
        const t = setTimeout(() => setCharCount((c) => c + 1), typeDelay);
        return () => clearTimeout(t);
      }
      const isLast = wordIndex === words.length - 1;
      const t = setTimeout(() => setPhase('holding'), isLast ? finalHoldDelay : holdDelay);
      return () => clearTimeout(t);
    }
    if (phase === 'holding') {
      setPhase('clearing');
      return;
    }
    if (phase === 'clearing') {
      if (charCount > 0) {
        const t = setTimeout(() => setCharCount((c) => c - 1), backDelay);
        return () => clearTimeout(t);
      }
      setWordIndex((i) => (i + 1) % words.length);
      setPhase('typing');
      return;
    }
  }, [
    reduceMotion,
    phase,
    charCount,
    wordIndex,
    words,
    prefix,
    typeDelay,
    backDelay,
    holdDelay,
    finalHoldDelay,
  ]);

  if (reduceMotion) {
    const first = words[0];
    if (!first) return null;
    const suffixStatic = first.en.slice(prefix.length);
    return (
      <p className="mkt-hero-tw__typewriter" data-testid="typewriter" aria-label={first.en} dir="ltr">
        <span className="mkt-hero-tw__pri">{prefix}</span>
        <span>{suffixStatic}</span>
      </p>
    );
  }

  const current = words[wordIndex];
  if (!current) return null;
  const suffix = current.en.slice(prefix.length);
  const visible = suffix.slice(0, charCount);

  return (
    <p className="mkt-hero-tw__typewriter" data-testid="typewriter" aria-label={current.en} dir="ltr">
      <span className="mkt-hero-tw__pri">{prefix}</span>
      <span>{visible}</span>
      <span className="mkt-hero-tw__cursor" aria-hidden="true" />
    </p>
  );
}
